import { Component, type ErrorInfo, type ReactNode } from 'react';
import { withTranslation, type WithTranslation } from 'react-i18next';
import { ContractError } from '@/lib/api/client';

interface State {
  error?: Error;
}

/**
 * Catches a render-time crash and shows something rather than a blank page.
 *
 * A class component because React has no hook equivalent — `componentDidCatch`
 * is the only way to catch an error thrown during render.
 *
 * `ContractError` gets its own treatment on purpose. It means the API returned a
 * payload the schema rejected, which is a deployment-skew problem — API and web
 * built from different commits — and it is worth naming, because the alternative
 * is somebody debugging a component that is fine.
 */
class ErrorBoundaryBase extends Component<{ children: ReactNode } & WithTranslation, State> {
  override state: State = {};

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // No error tracker yet — Phase 10 of the migration plan adds one. Until
    // then the console is the record, and it must include the component stack:
    // React 19 does not print it by default.
    console.error('Unhandled render error', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    const { t, children } = this.props;

    if (!error) {
      return children;
    }

    const isContractError = error instanceof ContractError;

    return (
      <div className="flex min-h-screen items-center justify-center bg-bg p-6">
        <div className="w-full max-w-lg rounded-lg border border-border-default bg-bg-raised p-5">
          <h1 className="text-sm font-semibold text-fg">{t('common.error')}</h1>

          {isContractError ? (
            <p className="mt-2 text-xs text-fg-muted">
              The API returned data this build does not understand. This usually means the API and
              the web app were built from different commits.
            </p>
          ) : null}

          <pre className="mt-3 max-h-64 overflow-auto rounded bg-bg-sunken p-3 text-xs text-fg-muted">
            {error.message}
          </pre>

          <button
            type="button"
            className="mt-4 rounded-md bg-accent px-4 py-2 text-sm font-medium text-accent-fg"
            onClick={() => window.location.reload()}
          >
            {t('common.retry')}
          </button>
        </div>
      </div>
    );
  }
}

export const ErrorBoundary = withTranslation()(ErrorBoundaryBase);
